import { useGetAuthMe, useGetMembersPlaces } from "@birdwatch/client";
import { useUser } from "@clerk/clerk-react";

import { env } from "@/env";
import { isPresaleZipcode, PRESALE_CAMPAIGN } from "@/features/shared/presale";
import type { PresaleView } from "../_types";

/** How many places to scan for the reserved one — a presale member has one or two. */
const PLACE_PAGE_SIZE = 20;

/**
 * The confirmation page's data, from the member's real account.
 *
 * `GET /members/places` returns each place WITH its purchase intent, so one authed request
 * answers both "which home did they reserve" and "is there an intent on it"; the
 * referral link is the member's real `referId`, the same one `ReferAMemberDialog`
 * shares. Signed-out visitors skip both requests and get the campaign copy.
 */
export function usePresaleView(): {
  data: PresaleView | undefined;
  isLoading: boolean;
} {
  const { user, isLoaded, isSignedIn } = useUser();
  const enabled = Boolean(isSignedIn);

  const placesQuery = useGetMembersPlaces()
    .query({ take: PLACE_PAGE_SIZE })
    .get({ enabled });
  const meQuery = useGetAuthMe().get({ enabled });

  if (!isLoaded || (enabled && (placesQuery.isLoading || meQuery.isLoading))) {
    return { data: undefined, isLoading: true };
  }

  const places = placesQuery.data ?? [];
  // The reserved home is the one in the campaign; fall back to the first place so a
  // member who bought from outside the zip list still sees their own address.
  const reserved =
    places.find((place) => isPresaleZipcode(place.zipcode, PRESALE_CAMPAIGN)) ??
    places[0];
  const referId = meQuery.data?.referId;

  return {
    isLoading: false,
    data: {
      firstName: user?.firstName ?? "",
      placeLabel: reserved?.address1 ?? "",
      spotsReserved: PRESALE_CAMPAIGN.spotsReservedFallback,
      referralUrl: referId ? `${env.PUBLIC_LINK_APP}/r/${referId}` : "",
      campaign: PRESALE_CAMPAIGN,
    },
  };
}
